import React, { useState, useRef } from "react";
import { ref, uploadBytes } from "firebase/storage";
import { storage } from "../../../../firebase";
import { Button } from "../../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
  DialogDescription,
} from "../../../../design/dialog";
import { Input } from "../../../../design/input";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  ErrorMessage,
  Description,
} from "../../../../design/fieldset";
import { useContract } from "../../../../contexts/ContractContext";
import { parseDateToTimestamp, todayLocalDateString } from "../../../../utils/date";
import { useCase } from "../../../../contexts/CaseContext";
import { useAccount } from "../../../../contexts/AccountContext";
import { storagePaths } from "@visitproof/types";
import { Select } from "../../../../design/select";
import { Textarea } from "../../../../design/textarea";
import {
  DocumentIcon,
  XMarkIcon,
  DocumentArrowUpIcon,
} from "@heroicons/react/24/outline";
import { Text } from "../../../../design/text";

interface Props {
  caseId: string;
}

interface Errors {
  file?: string;
  effectiveDate?: string;
  parentId?: string;
  percent?: string;
}

const CreateContractButton: React.FC<Props> = ({ caseId }) => {
  const { setSelectedCase, parents } = useCase();
  const { createContract } = useContract();
  const { account } = useAccount();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState<string>(
    todayLocalDateString(),
  );
  const [percent, setPercent] = useState<number>(50);
  const [parentId, setParentId] = useState<string | null>(null);
  const [comments, setComments] = useState<string>("");
  const [errors, setErrors] = useState<Errors>({});
  const [isUploading, setIsUploading] = useState(false);

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsUploading(true);

    try {
      const path = account?.id
        ? storagePaths.caseFile(account.id, caseId, file!.name)
        : `cases/${caseId}/${file!.name}`;
      const fileRef = ref(storage, path);
      await uploadBytes(fileRef, file!);
      await createContract({
        effectiveDate: parseDateToTimestamp(effectiveDate)!,
        caseId,
        path: fileRef.fullPath,
        parentId: parentId!,
        percent,
        comments,
      });
      setSelectedCase(caseId);
      clearForm();
    } catch (error) {
      setErrors({ file: "Failed to upload contract. Please try again." });
    }

    setIsUploading(false);
  };

  const validateForm = () => {
    const newErrors: Errors = {};
    if (!file) {
      newErrors.file = "Please upload a contract file.";
    }
    if (!effectiveDate) {
      newErrors.effectiveDate = "Effective date is required.";
    }
    if (!parentId) {
      newErrors.parentId = "Please select a parent.";
    }
    if (percent > 100 || percent < 0) {
      newErrors.percent = "Percent must be between 0 and 100.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const clearForm = () => {
    setFile(null);
    setPercent(50);
    setParentId(null);
    setComments("");
    setEffectiveDate(todayLocalDateString());
    setErrors({});
    setIsOpen(false);
  };

  const handleFileChange = (selectedFile: File | null) => {
    if (selectedFile) {
      setFile(selectedFile);
      setErrors((prev) => ({ ...prev, file: undefined }));
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileChange(droppedFile);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  return (
    <>
      <Button type="button" onClick={() => setIsOpen(true)}>
        Create Contract
      </Button>
      <Dialog open={isOpen} onClose={() => setIsOpen(false)} size="lg">
        <DialogTitle>Create Contract</DialogTitle>
        <DialogDescription>
          Upload a contract document and assign payment responsibilities.
        </DialogDescription>
        <DialogBody className="space-y-6">
          {/* File Upload Section */}
          <Field>
            <Label>Contract Document</Label>
            {!file ? (
              <div
                className={`relative flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors ${
                  isDragging
                    ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20"
                    : errors.file
                      ? "border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-900/10"
                      : "border-zinc-300 bg-zinc-50 hover:border-zinc-400 dark:border-zinc-700 dark:bg-zinc-800/50 dark:hover:border-zinc-600"
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <DocumentArrowUpIcon className="h-10 w-10 text-zinc-400 dark:text-zinc-500" />
                <Text className="mt-3 text-center text-sm text-zinc-600 dark:text-zinc-400">
                  <button
                    type="button"
                    className="font-semibold text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Click to upload
                  </button>{" "}
                  or drag and drop
                </Text>
                <Text className="mt-1 text-xs text-zinc-500 dark:text-zinc-500">
                  PDF, DOC, DOCX up to 10MB
                </Text>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) =>
                    handleFileChange(e.target.files?.[0] || null)
                  }
                />
              </div>
            ) : (
              <div className="flex items-center gap-3 rounded-lg border border-zinc-200 bg-zinc-50 p-3 dark:border-zinc-700 dark:bg-zinc-800/50">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-100 dark:bg-indigo-900/30">
                  <DocumentIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <Text className="truncate font-medium text-zinc-900 dark:text-zinc-100">
                    {file.name}
                  </Text>
                  <Text className="text-xs text-zinc-500">
                    {formatFileSize(file.size)}
                  </Text>
                </div>
                <button
                  type="button"
                  onClick={() => setFile(null)}
                  className="rounded-full p-1 hover:bg-zinc-200 dark:hover:bg-zinc-700"
                >
                  <XMarkIcon className="h-5 w-5 text-zinc-500" />
                </button>
              </div>
            )}
            {errors.file && <ErrorMessage>{errors.file}</ErrorMessage>}
          </Field>

          {/* Contract Details Section */}
          <Fieldset>
            <FieldGroup>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field>
                  <Label>Effective Date</Label>
                  <Input
                    type="date"
                    value={effectiveDate}
                    onChange={(e) => setEffectiveDate(e.target.value)}
                    disabled={isUploading}
                  />
                  {errors.effectiveDate && (
                    <ErrorMessage>{errors.effectiveDate}</ErrorMessage>
                  )}
                </Field>
                <Field>
                  <Label>Responsible Parent</Label>
                  <Select
                    value={parentId || ""}
                    onChange={(e) => setParentId(e.target.value)}
                    disabled={isUploading}
                  >
                    <option value="">Select a parent</option>
                    {parents.map((parent) => (
                      <option key={parent.id} value={parent.id}>
                        {parent.firstName} {parent.lastName}
                      </option>
                    ))}
                  </Select>
                  {errors.parentId && (
                    <ErrorMessage>{errors.parentId}</ErrorMessage>
                  )}
                </Field>
              </div>
              <Field>
                <Label>Payment Share (%)</Label>
                <Description>
                  Percentage of visit costs this parent is responsible for
                </Description>
                <div className="flex items-center gap-4">
                  <Input
                    type="range"
                    min="0"
                    max="100"
                    value={percent}
                    onChange={(e) => setPercent(parseInt(e.target.value))}
                    disabled={isUploading}
                    className="flex-1"
                  />
                  <div className="w-16 text-center">
                    <span className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                      {percent}%
                    </span>
                  </div>
                </div>
                {errors.percent && (
                  <ErrorMessage>{errors.percent}</ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Notes (Optional)</Label>
                <Textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder="Add any relevant notes about this contract..."
                  rows={3}
                  disabled={isUploading}
                />
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={clearForm} disabled={isUploading}>
            Cancel
          </Button>
          <Button
            disabled={!file || !parentId || isUploading}
            onClick={handleSubmit}
          >
            {isUploading ? "Uploading..." : "Create Contract"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateContractButton;
